import type { ConfigPayload } from '@/shared/protocol';

import { CHAT_BOT_ROUTES, httpClient } from '@/lib/api';

import { CONFIG_FETCH_TIMEOUT_MS } from './constants';

/**
 * Fetch the chatbot-config for the supplied API key. Returns `null` on any
 * failure (network, non-2xx, malformed JSON, missing required fields). On
 * failure the launcher skips bubble injection entirely — there is no
 * client-side DEFAULT_CONFIG fallback for a real render.
 *
 * HTTP failures are auto-logged by the shared `httpClient` via the
 * module-scope error logger bound at launcher boot (`launcher/index.ts`).
 *
 * Empty apiKey → skip the fetch (BE would 401).
 */
export async function fetchChatbotConfig(
  apiKey: string,
  fetcher: typeof globalThis.fetch,
): Promise<ConfigPayload | null> {
  if (!apiKey) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT_MS);
  try {
    const { response } = await httpClient({
      apiKey,
      fetcher,
      method: 'GET',
      path: CHAT_BOT_ROUTES.config,
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const body = (await response.json()) as Partial<ConfigPayload>;
    // Narrow validation — only fields the launcher itself reads, so a
    // forward-compatible BE addition doesn't trip rejection.
    if (typeof body.agent?.name !== 'string') return null;
    if (typeof body.branding?.primaryColor !== 'string') return null;
    if (typeof body.branding.position !== 'string') return null;
    return body as ConfigPayload;
  } catch (error) {
    // eslint-disable-next-line no-console -- diagnostic for embed integrators
    console.warn('[circuit-chat-embed] /chat-bot/config fetch failed:', error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}
